/* ─────────────────────────────────────────────────────────
 *  TableOps – useTableOpsSounds
 *
 *  Thin React surface over {@link tableOpsSoundService}. Exposes two
 *  declarative cue triggers plus a persisted mute flag. Components
 *  stay pure — they call `playOrderEnter()` / `playStationClose()`
 *  and never touch the Web Audio graph.
 *
 *  Library-first, motif-fallback:
 *  - On mount it tries to resolve a MIDI-DAW *library element* (by
 *    project name) for each cue. If an operator authored one, it
 *    transparently replaces the built-in motif for every subsequent
 *    play. If absent / forbidden / offline, the built-in motif is used.
 *  - Resolution is cached and failure-silent (a missing library or a
 *    403 for users without MIDI-DAW access degrades to the motif).
 * ───────────────────────────────────────────────────────── */
import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchData } from '@/utils/fetch'
import type { DawMidiFile } from '../../midi-daw/types/midi-daw-types'
import { DEFAULT_PATCHES } from '../../midi-daw/types/midi-daw-types'
import { listMidiProjects, loadMidiVersionSnapshot, type MidiProjectSummary } from '../../midi-daw/api/midiLibraryApi'
import { parseSnapshot } from '../../midi-daw/services/project-snapshot'
import { tableOpsSoundService } from './tableOpsSoundService'
import { CUE_ORDER_ENTER, CUE_STATION_CLOSE } from './cueMotifs'

/** MIDI-DAW library project names an operator can author to override a cue. */
const ORDER_ENTER_PROJECT = 'Airport Asc - Maj Triad'
const STATION_CLOSE_PROJECT = 'Airport Desc - Maj Triad'

// Friendly aliases seen in real project naming styles.
const ORDER_ENTER_ALIASES = [
  ORDER_ENTER_PROJECT,
  'Airport_Asc_-_Maj_Triad',
]
const STATION_CLOSE_ALIASES = [
  STATION_CLOSE_PROJECT,
  'Airport_Desc_-_Maj_Triad',
]

const MUTE_STORAGE_KEY = 'tableops.sound.muted'
const SOUND_SETTINGS_URL = '/bff/table-ops/sound-settings'

function clampVolume(value: number): number {
  if (!Number.isFinite(value)) return 0.7
  return Math.max(0, Math.min(1, value))
}

export type TableOpsCueAction = 'orderEnter' | 'orderComplete'

export interface TableOpsCueBindings {
  orderEnterProjectKey: string | null
  orderCompleteProjectKey: string | null
}

export interface TableOpsSoundSettings {
  cueBindings: TableOpsCueBindings
  globalVolume: number
}

export const DEFAULT_CUE_BINDINGS: TableOpsCueBindings = {
  orderEnterProjectKey: null,
  orderCompleteProjectKey: null,
}

export const DEFAULT_GLOBAL_VOLUME = 0.7

function loadMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function persistMuted(value: boolean): void {
  try {
    window.localStorage.setItem(MUTE_STORAGE_KEY, value ? '1' : '0')
  } catch {
    /* storage unavailable — runtime mute still applies via the service */
  }
}

async function loadSoundSettingsFromServer(): Promise<TableOpsSoundSettings> {
  try {
    const raw = await fetchData(SOUND_SETTINGS_URL)
    return {
      cueBindings: {
        orderEnterProjectKey: typeof raw?.order_enter_project_key === 'string' ? raw.order_enter_project_key : null,
        orderCompleteProjectKey: typeof raw?.order_complete_project_key === 'string' ? raw.order_complete_project_key : null,
      },
      globalVolume: clampVolume(typeof raw?.global_volume === 'number' ? raw.global_volume : DEFAULT_GLOBAL_VOLUME),
    }
  } catch {
    return {
      cueBindings: DEFAULT_CUE_BINDINGS,
      globalVolume: DEFAULT_GLOBAL_VOLUME,
    }
  }
}

async function persistSoundSettingsToServer(value: TableOpsSoundSettings): Promise<void> {
  await fetchData(SOUND_SETTINGS_URL, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      order_enter_project_key: value.cueBindings.orderEnterProjectKey,
      order_complete_project_key: value.cueBindings.orderCompleteProjectKey,
      global_volume: value.globalVolume,
    }),
  })
}

function normalizeProjectName(name: string): string {
  return String(name || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function pickProjectByAliases(
  projects: MidiProjectSummary[],
  aliases: string[],
): MidiProjectSummary | null {
  if (!projects.length) return null
  const aliasSet = new Set(aliases.map(normalizeProjectName))
  const candidates = projects.filter((p) => aliasSet.has(normalizeProjectName(p.name)))
  if (!candidates.length) return null
  candidates.sort((a, b) => Date.parse(b.updated_at || '') - Date.parse(a.updated_at || ''))
  return candidates[0] ?? null
}

export interface UseTableOpsSounds {
  /** Fire the "new order arrived" cue. */
  playOrderEnter: () => void
  /** Fire the "order completed / handed-off" cue. */
  playOrderComplete: () => void
  /** Backward-compat alias for existing callers. */
  playStationClose: () => void
  /** Whether cues are currently muted. */
  muted: boolean
  /** Toggle / set the persisted mute flag. */
  setMuted: (value: boolean) => void
  /** Global master volume for cue playback, from 0 to 1. */
  globalVolume: number
  /** Persist and apply the global master volume. */
  setGlobalVolume: (value: number) => void
  /** MIDI library projects available for cue selection. */
  libraryProjects: MidiProjectSummary[]
  libraryLoading: boolean
  libraryError: string | null
  refreshLibrary: () => Promise<void>
  cueBindings: TableOpsCueBindings
  setCueProject: (action: TableOpsCueAction, projectKey: string | null) => void
}

export function useTableOpsSounds(): UseTableOpsSounds {
  const [muted, setMutedState] = useState<boolean>(loadMuted)
  const [cueBindings, setCueBindings] = useState<TableOpsCueBindings>(DEFAULT_CUE_BINDINGS)
  const [globalVolume, setGlobalVolumeState] = useState<number>(DEFAULT_GLOBAL_VOLUME)
  const [libraryProjects, setLibraryProjects] = useState<MidiProjectSummary[]>([])
  const [libraryLoading, setLibraryLoading] = useState(false)
  const [libraryError, setLibraryError] = useState<string | null>(null)

  // Active cue sources — initialised to built-in motifs, swapped to
  // library elements once (and if) they resolve.
  const orderEnterFile = useRef<DawMidiFile>(CUE_ORDER_ENTER)
  const stationCloseFile = useRef<DawMidiFile>(CUE_STATION_CLOSE)

  // Keep the singleton service in sync with the persisted flag on mount.
  useEffect(() => {
    tableOpsSoundService.setMuted(muted)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    tableOpsSoundService.setMasterVolume(globalVolume)
  }, [globalVolume])

  const setMuted = useCallback((value: boolean) => {
    setMutedState(value)
    persistMuted(value)
    tableOpsSoundService.setMuted(value)
  }, [])

  const refreshLibrary = useCallback(async () => {
    setLibraryLoading(true)
    setLibraryError(null)
    try {
      const items = await listMidiProjects()
      setLibraryProjects(items)
    } catch (err) {
      setLibraryError(err instanceof Error ? err.message : 'Failed to load MIDI library')
    } finally {
      setLibraryLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshLibrary()
  }, [refreshLibrary])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const loaded = await loadSoundSettingsFromServer()
      if (cancelled) return
      setCueBindings(loaded.cueBindings)
      setGlobalVolumeState(loaded.globalVolume)
      tableOpsSoundService.setMasterVolume(loaded.globalVolume)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const persistSettings = useCallback((nextCueBindings: TableOpsCueBindings, nextGlobalVolume: number) => {
    void persistSoundSettingsToServer({
      cueBindings: nextCueBindings,
      globalVolume: nextGlobalVolume,
    }).catch(() => {})
  }, [])

  const setCueProject = useCallback((action: TableOpsCueAction, projectKey: string | null) => {
    setCueBindings((prev) => {
      const next: TableOpsCueBindings = {
        ...prev,
        orderEnterProjectKey: action === 'orderEnter' ? projectKey : prev.orderEnterProjectKey,
        orderCompleteProjectKey: action === 'orderComplete' ? projectKey : prev.orderCompleteProjectKey,
      }
      persistSettings(next, globalVolume)
      return next
    })
  }, [globalVolume, persistSettings])

  const setGlobalVolume = useCallback((value: number) => {
    const next = clampVolume(value)
    setGlobalVolumeState(next)
    tableOpsSoundService.setMasterVolume(next)
    persistSettings(cueBindings, next)
  }, [cueBindings, persistSettings])

  // Resolve cue source MIDI on library/binding changes. Failure-silent by design.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const resolveByVersionKey = async (
        latestVersionKey: string,
        target: React.MutableRefObject<DawMidiFile>,
      ) => {
        if (!latestVersionKey) return false
        try {
          const snap = await loadMidiVersionSnapshot(latestVersionKey)
          const parsed = parseSnapshot(snap?.snapshot)
          if (!cancelled && parsed?.midiFile?.tracks.length) {
            target.current = parsed.midiFile
            return true
          }
        } catch {
          /* keep fallback */
        }
        return false
      }

      const resolve = async (
        explicitProjectKey: string | null,
        aliases: string[],
        target: React.MutableRefObject<DawMidiFile>,
        builtin: DawMidiFile,
      ) => {
        target.current = builtin
        const explicit = explicitProjectKey
          ? libraryProjects.find((p) => p.project_key === explicitProjectKey)
          : null
        if (explicit?.latest_version_key) {
          await resolveByVersionKey(explicit.latest_version_key, target)
          return
        }
        const match = pickProjectByAliases(libraryProjects, aliases)
        if (!match?.latest_version_key) return
        await resolveByVersionKey(match.latest_version_key, target)
      }

      await Promise.all([
        resolve(cueBindings.orderEnterProjectKey, ORDER_ENTER_ALIASES, orderEnterFile, CUE_ORDER_ENTER),
        resolve(cueBindings.orderCompleteProjectKey, STATION_CLOSE_ALIASES, stationCloseFile, CUE_STATION_CLOSE),
      ])
    })()
    return () => {
      cancelled = true
    }
  }, [cueBindings.orderCompleteProjectKey, cueBindings.orderEnterProjectKey, libraryProjects])

  const playOrderEnter = useCallback(() => {
    tableOpsSoundService.play(orderEnterFile.current, DEFAULT_PATCHES.sine)
  }, [])

  const playOrderComplete = useCallback(() => {
    tableOpsSoundService.play(stationCloseFile.current, DEFAULT_PATCHES.sine)
  }, [])

  const playStationClose = playOrderComplete

  return {
    playOrderEnter,
    playOrderComplete,
    playStationClose,
    muted,
    setMuted,
    globalVolume,
    setGlobalVolume,
    libraryProjects,
    libraryLoading,
    libraryError,
    refreshLibrary,
    cueBindings,
    setCueProject,
  }
}
